import { createSelector } from '@reduxjs/toolkit';

import filtersChoiceSelector from '@/features/choice/selectors/filters';
import { configSelector } from '@/features/config/selectors';
import flattenFilterGridItemsSelector from '@/features/config/selectors/flattenFilterGridItems';
import { getFilterKeysToReset } from '@/features/filters/helpers/getDependantFiltersToReset';
import { FilterItemType } from '@/features/filters/types';
import type { RootStateType } from '@/features/store';
import { FiltersDataType } from '@/interfaces/filters.interfaces';
import { II18Service } from '@/services/I18Service';
import InjectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';
import isDefined from '@/utils/isDefined';

import prepareFilterItemProps from '../helpers/prepareFilterItemProps';
import prepareFilterItemValue from '../helpers/prepareFilterItemValue';

/**
 * Получить список фильтров, преобразовав настройки фильтров
 */
const filterItemsSelector = createSelector(
  [
    (_: RootStateType, { filtersData }: { filtersData?: FiltersDataType }) => filtersData,
    flattenFilterGridItemsSelector,
    filtersChoiceSelector,
    configSelector,
  ],
  (filtersData, filterGridItems, filtersChoice, config) => {
    const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);

    const dependentFilterItemKeysMap = new Map<
      FilterItemType['key'],
      FilterItemType['key'][]
    >();
    // Hack: собираем мап, который будет содержать массив фильтров, которые нужно сбросить
    // если данный фильтр изменится.
    const resetFilterItemKeysMap = new Map<
      FilterItemType['key'],
      FilterItemType['key'][]
    >();
    const extendedFilterGridItems = filterGridItems.map(item => {
      const props = prepareFilterItemProps(
        {
          type: item.type,
          props: item.props,
          filterValues: filtersData?.[item.key],
        },
        config,
      );
      const value = prepareFilterItemValue({
        type: item.type,
        filterChoiceValue: filtersChoice?.[item.key],
        filterValues: filtersData?.[item.key],
      });

      resetFilterItemKeysMap.set(
        item.key,
        getFilterKeysToReset(item.key, filterGridItems),
      );

      item?.dependencies?.forEach(dependentFilterItemKey => {
        dependentFilterItemKeysMap.set(dependentFilterItemKey, [
          ...(dependentFilterItemKeysMap.get(dependentFilterItemKey) || []),
          item.key,
        ]);
      });

      return {
        ...item,
        props,
        value,
      };
    });

    return extendedFilterGridItems.map<FilterItemType>(
      ({
        key,
        path,
        type,
        props,
        value,
        dependencies,
        groupByKey,
        dependencyTextFormatter,
      }) => {
        const dependentKeys = dependentFilterItemKeysMap.get(key);

        // Hack: Для того, чтобы сбросить фильтры, которые должны быть сброшены, при
        // изменении текущего фильтра, добавляем их ключи в качестве зависимостей.
        const dependentKeysSet = new Set(resetFilterItemKeysMap.get(key));
        dependentKeys?.forEach(key => dependentKeysSet.add(key));
        const dependents = Array.from(dependentKeysSet.values());

        /* Dependency */
        const dependentFilterItems = extendedFilterGridItems.filter(filterItem =>
          dependencies?.includes(filterItem.key),
        );
        const unselectedDependentFilterItems = dependentFilterItems.filter(
          filterItem => !isDefined(filterItem.value),
        );
        const isDependencyFulfilled = !unselectedDependentFilterItems.length;
        const dependencyText =
          dependencyTextFormatter?.(unselectedDependentFilterItems) ??
          `${i18n.t('filterItem.dependencyText')}`;

        /* Group */
        const groupingFilterItem = extendedFilterGridItems.find(
          filterItem => filterItem.key === groupByKey,
        );
        const isGroupFulfilled =
          !!groupByKey && !!groupingFilterItem && isDefined(groupingFilterItem.value);

        return {
          key,
          path,
          value,
          type,
          props,
          dependents,
          dependencies,
          dependencyText,
          isDependencyFulfilled,
          groupByKey,
          isGroupFulfilled,
        };
      },
    );
  },
);

export default filterItemsSelector;
